/**
 * WorkingIndicator - the mark, and the engine's own phrases when it has them.
 *
 * What used to sit here was a named-stage stepper: "Reading the question ✓",
 * "Choosing sources ✓", "Searching sources 1m27s", then a "Research completed
 * in 1m19s · 4 stages" summary. It read as machinery - a fixed pipeline
 * printed at the user - and it aged badly every time the engine changed its
 * stages.
 *
 * What replaced it briefly was a generic vocabulary - "Thinking…",
 * "Working…", "Checking…" - which was the same mistake one size smaller: a
 * word the client made up, saying nothing the reader could not already see.
 * It is gone. There are now exactly two states:
 *
 *   no cues yet  → the brand mark alone, beside the skeleton. Silent.
 *   cues arrived → the engine's phrases for THIS question ("Checking the
 *                  2019 amendments"), cycling at random.
 *
 * A turn that never sends cues - a fast path, a "thanks" - simply stays
 * silent for its second or two and lets its acknowledgement line do the
 * talking. Nothing is invented to fill the gap, ever: the only words on this
 * line are words the engine wrote knowing what it was about to do.
 *
 * No checkmarks, no per-stage timers, no completion summary - this unmounts
 * the moment the answer is on screen.
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import BrandLoader from '../../shared/brand/BrandLoader';

/**
 * How long a phrase holds, randomised per switch.
 *
 * A metronome reads as a countdown; an uneven beat reads as someone working.
 */
export const HOLD_MIN_MS = 2500;
export const HOLD_MAX_MS = 4000;

/** The cross-fade at each switch. Long enough to see, short enough to ignore. */
export const FADE_MS = 260;

/** A phrase from the pool, never the one already on screen. */
export function pickWord(pool: readonly string[], current?: string | null): string {
  const usable = pool.filter((w) => w && w !== current);
  if (!usable.length) return pool[0] || '';
  return usable[Math.floor(Math.random() * usable.length)];
}

/** A hold somewhere in the range, uniformly. */
export function pickHold(): number {
  return HOLD_MIN_MS + Math.random() * (HOLD_MAX_MS - HOLD_MIN_MS);
}

interface Props {
  /**
   * The engine's phrases for this question, as they arrive. Empty is the
   * normal starting state and the normal fast-path state - not an error, and
   * not something to paper over.
   */
  cues?: string[];
  /** Hold the first phrase (tests, and any caller that wants it still). */
  cycle?: boolean;
  className?: string;
}

const WorkingIndicator: React.FC<Props> = ({ cues, cycle = true, className }) => {
  const { t } = useTranslation();
  /** Null means "mark only" - the state every run starts in. */
  const [word, setWord] = React.useState<string | null>(null);
  /** False for the length of the cross-fade, while the old phrase leaves. */
  const [shown, setShown] = React.useState(true);

  const pool = React.useMemo(
    () => (cues || []).map((c) => String(c).trim()).filter(Boolean),
    [cues]
  );
  /**
   * Identity of the pool's CONTENT, not of its array. A re-render that
   * rebuilds the same list must not restart the cycle mid-phrase.
   */
  const poolKey = pool.join('|');

  /* The phrase on screen, readable synchronously by the timer effect below.
     Declared first so it is current by the time that effect runs. */
  const wordRef = React.useRef<string | null>(null);
  React.useEffect(() => {
    wordRef.current = word;
  }, [word]);

  React.useEffect(() => {
    if (!pool.length) return;

    let hold: ReturnType<typeof setTimeout>;
    let fade: ReturnType<typeof setTimeout>;

    /* Recursive timeouts rather than an interval: each hold is its own
       length, and the swap happens at the bottom of the fade so the two
       phrases never overlap. */
    const schedule = () => {
      hold = setTimeout(() => {
        setShown(false);
        fade = setTimeout(() => {
          setWord((current) => pickWord(pool, current));
          setShown(true);
          schedule();
        }, FADE_MS);
      }, pickHold());
    };

    const current = wordRef.current;
    if (current !== null && !pool.includes(current)) {
      /* The cue list changed under it - a re-run, or a late correction.
         Move to the new vocabulary NOW rather than at the end of this hold,
         through the same fade as any other switch. */
      setShown(false);
      fade = setTimeout(() => {
        setWord(pickWord(pool));
        setShown(true);
        if (cycle) schedule();
      }, FADE_MS);
      return () => {
        clearTimeout(hold);
        clearTimeout(fade);
      };
    }

    if (current === null) {
      // The first phrase of the run: the mark is already turning, so it
      // fades up beside it rather than cutting in.
      setWord(pickWord(pool));
      setShown(true);
    }

    if (cycle) schedule();
    return () => {
      clearTimeout(hold);
      clearTimeout(fade);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [poolKey, cycle]);

  return (
    <div
      className={`mb-3 flex items-center gap-2 ${className || ''}`}
      data-testid="working-indicator"
      data-has-cues={pool.length ? 'true' : 'false'}
      role="status"
      aria-live="polite"
    >
      {/* The brand mark drawing itself - the same animation every AI process
          in the app runs. `aria-hidden` because it carries its own
          `role="status"`, and this line is already one. */}
      <span className="flex-shrink-0 flex items-center" aria-hidden>
        <BrandLoader size="glyph" />
      </span>
      {word === null ? (
        // Silent on screen, but not to a screen reader: "something is
        // happening here" is the one thing worth saying without the engine.
        <span className="sr-only">{t('Working')}</span>
      ) : (
        /* One element, deliberately: the text swaps at the bottom of the fade
           so the same node carries it out and back in. Re-keying per phrase
           would remount it and the transition would never run. The phrase is
           rendered VERBATIM - the engine wrote it in the session's output
           language, and running it through `t()` would be a category error. */
        <span
          data-testid="working-word"
          data-shown={shown ? 'true' : 'false'}
          className={[
            'text-sm text-fg-muted select-none reveal-item',
            'transition-all duration-[260ms] ease-slide',
            shown ? 'opacity-100 translate-y-0 blur-0' : 'opacity-0 translate-y-1 blur-[2px]',
          ].join(' ')}
        >
          {word}…
        </span>
      )}
    </div>
  );
};

export default WorkingIndicator;
